/**
 * Events.js is a utility file that contains functions to filter and prepare event data.
 */
import { getSeverityColour, getSeverityColourClassNames, getSeverityHexColour, convertInputToTimezoneAwareISO, convertDateToLocal, getEntityColour } from "@datahooks/metadata/DataUtils";
import StyledTooltip from "@components/general/StyledTooltip";

/**
 * Map for event type IDs to their corresponding icon components.
 */
export const EVENT_ICONS = Object.freeze({
  11: "/assets/icons/rocket.svg",
  2:  "/assets/icons/freeze.svg",
  3:  "/assets/icons/wrench.svg",
  4:  "/assets/icons/boom.svg",
  1:  "/assets/icons/exclamation.svg",
  default: "/assets/icons/exclamation.svg",
});

/**
 * Get the icon and severity color for an event.
 *
 * @param {Object} props
 * @param {import("@components/cards/events/events.types").ProjectEvent} props.event - The event data
 * @returns {Object} - Object containing iconPath, severityColour, severityColourClassName, and severityColourHex
 */
export const getEventVisual = (event) => {
  const iconPath = EVENT_ICONS[event.event_type_id] ?? EVENT_ICONS.default;
  return {
    iconPath,
    severityColour: severityColours[event.severity],
    severityColourClassName: severityColourClassNames[event.severity],
    severityColourHex: severityColourHex[event.severity],
  };
};

//Convert event severities to existing colours
export const severityColours = {
  low: getSeverityColour("MINOR"),
  medium: getSeverityColour("MAJOR"),
  high: getSeverityColour("CRITICAL"),
  critical: getSeverityColour("BLOCKER"),
};

export const severityColourClassNames = {
  low: getSeverityColourClassNames("MINOR"),
  medium: getSeverityColourClassNames("MAJOR"),
  high: getSeverityColourClassNames("CRITICAL"),
  critical: getSeverityColourClassNames("BLOCKER"),
};

export const severityColourHex = {
  low: getSeverityHexColour("MINOR"),
  medium: getSeverityHexColour("MAJOR"),
  high: getSeverityHexColour("CRITICAL"),
  critical: getSeverityHexColour("BLOCKER"),
};

//Rank the severity of events
export const severityRank = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

/**
 * Filter events based on the given filter type.
 * 
 * Filtering logic:
 *   Active: event.start_time <= now and (end_time is null or end_time > now)
 *   Inactive: events that don't meet the active criteria
 *   Both: no filtering
 * 
 * @param {Array} events - Array of event objects.
 * @param {string} filterType - "active", "inactive", or "both".
 * @param {Date} releaseEndTime - The end time of the release
 * @returns {Array} - Filtered events.
 */
export function filterEvents(events, filterType, releaseEndTime) {

  const enrichedEvents = (events || []).map(event => {  
    const eventStart = new Date(event.start_time);
    const eventEnd = event.end_time ? new Date(event.end_time) : null; // if no end_time, set to null

    const isActive = eventStart <= releaseEndTime && (!eventEnd || eventEnd > releaseEndTime);

    return { ...event, isActive };
  });

  if (filterType === 'both') return enrichedEvents;

  return enrichedEvents.filter(event => (filterType === "active") ? event.isActive : !event.isActive);
}

/**
 * Build severity chart data from the filtered events.
 * @param {Array} events - Array of filtered events.
 * @returns {Array} - Array of objects for the severity chart.
 */
export function buildSeverityChartData(events) {
  const severityCounts = events.reduce((acc, event) => {
    acc[event.severity] = (acc[event.severity] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(severityCounts).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
    color: severityColours[key],
  }));
}

/**
 * Build event title chart data from the filtered events.
 * Set colour by event type ID.
 * @param {Array} events - Array of filtered events.
 * @returns {Array} - Array of objects for the event title chart.
 */
export function buildEventTitleChartData(events) {
  const eventTitleCounts = {};
  const titleToEventTypeId = {};
  
  // Count events and map titles to their event_type_id
  events.forEach(event => {
    const title = event.title;
    eventTitleCounts[title] = (eventTitleCounts[title] || 0) + 1;
    
    // For "Other", always use a consistent event_type_id regardless of actual ID or event level
    if (title === "Other") {
      titleToEventTypeId[title] = 1;
    } else if (!titleToEventTypeId[title]) {
      titleToEventTypeId[title] = event.event_type_id;
    }
  });

  // Convert to chart data format with colours
  return Object.entries(eventTitleCounts).map(([title, count]) => ({
    name: title,
    value: count,
    color: getEventTypeColour(title, titleToEventTypeId[title]),
  }));
}

/**
 * Build severity chart data from metrics data with colours
 * @param {Array} severityBreakdown - Array of severity metrics
 * @returns {Array} - Array of objects for the severity chart with colors
 */
export function buildSeverityChartDataFromMetrics(severityBreakdown) {
  return severityBreakdown.map(item => ({
    name: item.severity.charAt(0).toUpperCase() + item.severity.slice(1),
    count: item.count,
    color: severityColours[item.severity] || severityColours.low, // fallback to low severity colour
  }));
}

/**
 * Build event type chart data from metrics API data with colours
 * @param {Array} eventTypes - Array of event type metrics from API
 * @returns {Array} - Array of objects for the event type chart with colours
 */
export function buildEventTypeChartDataFromMetrics(eventTypes) {
  return eventTypes.map(item => ({
    name: item.event_type.length > 20 ? item.event_type.substring(0, 20) + '...' : item.event_type,
    count: item.count,
    color: getEventTypeColour(item.event_type, item.event_type_id || 1),
  }));
}

/**
 * Prepare tracker data for a given number of days.
 * For each day, if one or more events are active (i.e. the day overlaps with the event’s duration),
 * use the colour of the highest severity event and create a formatted tooltip with the day/date
 * and each event title on a new line. Also, include the duration (in days) of the highest severity event.
 *
 * @param {Array} events - Array of filtered events.
 * @param {number} daysCount - Number of days to include (including today).
 * @returns {Array} - Tracker data array with additional "length" property.
*/
export function buildTrackerData(events, daysCount) {
  const trackerData = [];
  const today = new Date();
  let dayColour = "green"; // Default colour for days with no events

  for (let i = daysCount - 1; i >= 0; i--) {
    // Create a date for the day in question (set time to start of day)
    const dayDate = new Date(today);
    dayDate.setHours(0, 0, 0, 0);
    dayDate.setDate(today.getDate() - i);
    const dayKey = dayDate.toISOString().split("T")[0];

    // Define day boundaries for overlap checking.
    const dayStart = new Date(dayKey);
    const dayEnd = new Date(dayKey);
    dayEnd.setDate(dayEnd.getDate() + 1);

    // Filter events active on this day.
    const eventsForDay = events.filter((event) => {
      const eventStart = new Date(event.start_time);
      const eventEnd = event.end_time ? new Date(event.end_time) : null;
      return eventStart < dayEnd && (!eventEnd || eventEnd >= dayStart);
    });

    let tooltip = "";
    let length = 0;

    if (eventsForDay.length > 0) {
      // Determine the highest severity event among those active on this day.
      const highestEvent = eventsForDay.reduce((prev, curr) =>
        severityRank[curr.severity] > severityRank[prev.severity] ? curr : prev, eventsForDay[0]
      );

      dayColour = getMostSevereEventColour(eventsForDay);

      // Create a formatted tooltip:
      // Include the date, then list each event title on its own line with a bullet.
      tooltip =
        `Date: ${dayKey}\nEvents:\n` +
        eventsForDay.map((e) => `• ${e.title}`).join("\n");

      // Calculate the duration (in days) of the highest severity event.
      const eventStart = new Date(highestEvent.start_time);
      const eventEnd = highestEvent.end_time ? new Date(highestEvent.end_time) : null;
      if (eventEnd) {
        length = Math.ceil((eventEnd - eventStart) / (1000 * 60 * 60 * 24));
      } else {
        // If no end time is provided, consider the event as lasting 1 day (or ongoing).
        length = 1;
      }
    }

    trackerData.push({
      date: dayKey,
      value: eventsForDay.length > 0 ? 1 : 0,
      color: dayColour,
      tooltip,
      length,
    });
  }
  return trackerData;
}

/**
 * Concatenates unique event titles and truncates if they exceed max length
 * @param {Array} events - Array of event objects with title property
 * @param {number} maxLength - Maximum length of the output string
 * @param {string} [truncationSuffix="..."] - Text to append when truncated
 * @return {string} - Concatenated, deduplicated, and possibly truncated titles
*/
export function formatEventTitles(events = [], maxLength = 30, truncationSuffix = "...") {
  if (!events.length) return "";

  // Extract unique titles - Set removes duplicates, then join into a single string
  let uniqueTitles = [...new Set(events.map(event => event.title))].join(", ");

  // Truncate if needed
  if (uniqueTitles.length > maxLength) {
    // Find the last complete title that fits within maxLength
    let lastIndex = uniqueTitles.lastIndexOf(", ", maxLength - truncationSuffix.length);

    // If no space found, just cut at maxLength
    if (lastIndex === -1) {
      lastIndex = maxLength - truncationSuffix.length;
    }

    uniqueTitles = uniqueTitles.substring(0, lastIndex) + truncationSuffix;
  }

  return uniqueTitles;
}

/**
 * Merges events with test items based on matching test ID and additional ID.
 * Ordered by test items order to match the original order.
 * 
 * @param {Array} events - Array of event objects.
 * @param {Array} testItems - Array of test item objects.
 * @returns {Array} - Array of event objects with added testName and area properties.
 */
export function mergeEventsWithTestItems(events = [], testItems = [], additionalIdName = "") {
  const eventMap = new Map();

  // Build a map of events keyed by test_id (and additional ID if provided)
  events.forEach((event) => {
    const key = additionalIdName 
      ? `${event.test_id}__${event.additional_id}` 
      : `${event.test_id}`;
    if (!eventMap.has(key)) {
      eventMap.set(key, []);
    }
    eventMap.get(key).push(event);
  });

  // For each test item, build the key in the same way and get matching events
  return testItems.flatMap((testItem) => {
    const key = additionalIdName 
      ? `${testItem.id}__${testItem[additionalIdName]}` 
      : `${testItem.id}`;
    const matchingEvents = eventMap.get(key) || [];

    // For each matching event, add extra details from the test item.
    return matchingEvents.map((event) => ({
      ...event,
      testName: testItem.name,
      // Include the additional ID value if provided.
      ...(additionalIdName ? { additionalId: testItem[additionalIdName] } : {}),
    }));
  });
}

/**
 * Function get the colour of the most severe event in a list of events
 * @param {Array} events - Array of event objects
 * @returns {string} - Colour of the most severe event
 */
export function getMostSevereEventColour(events) {
  if (!events || events.length === 0) return "bg-green-800 text-white";
  if (!hasOpenEvents(events)) return "bg-green-800 text-white";

  const mostSevereEvent = events.reduce((prev, curr) =>
    severityRank[curr.severity] > severityRank[prev.severity] ? curr : prev, events[0]
  );
  return severityColourClassNames[mostSevereEvent.severity] || "green";
}

/**
 * Function to return the open status of all the events in the array. If the array has an open event
 * we return true, if all events have an end time, we return false.
 * 
 * @param {Array} events - Array of event objects
 * 
 * @returns {Boolean} - True if there are open events, false otherwise
 */
export function hasOpenEvents(events) {
  if (!events || events.length === 0) return false;

  // Check if any event is open (i.e., has no end time)
  return events.some(event => !event.end_time);
}

/**
 * Function to return the start time tooltip for an event, depending on the event type and status of the event
 * 
 * @param {Object} eventStart - Event start time
 * @param {Object} eventRelease - Event release information
 * @param {Object} eventLevel - Event level information (e.g., "Test", "Project" etc.)
 * 
 * @returns {String} - Formatted start time tooltip
 */
export function getStartTimeTooltip({eventStart, eventRelease, eventLevel}) {
  if (eventLevel === 'Test') {
    return `Started in ${eventRelease}`;
  } else {
    return `Started at ${eventStart}`;
  }
}

/** 
  * Function to return the end time tooltip for an event, depending on the event type and status of the event
  * 
  * @param {string} eventEnd - Event end time
  * @param {string} eventRelease - Event release information
  * @param {Object} eventLevel - Event level information (e.g., "Test", "Project" etc.)
  * 
  * @returns {String} - Formatted end time tooltip
  **/
export function getEndTimeTooltip(eventEnd, eventRelease, eventLevel) {
  if (eventEnd && eventEnd != '-') {
    if (eventLevel === 'Test') {
      return `Resolved in ${eventRelease} at ${eventEnd}`;
    } else  {
        return `Resolved at ${eventEnd}`;
    }
  }

  return "Open"
}

/**
 * Renders the end time column cell for a given event.
 *
 * Logic:
 * - Only renders if the event is not a Test, OR it is a Test but not active.
 * - If end_time is present:
 *   - Tooltip content varies based on event level and active status.
 *   - Value is always `formatted_end_time`.
 * - If no end_time is present:
 *   - Shows a dash.
 *
 * @param {string} formattedEndTime - The formatted end time to display
 * @param {string} endRelease - The release label for test events
 * @param {string} eventLevel - "Test" or something else
 * @param {string} colClass - The Tailwind/CSS class to apply to the column
 * @returns {JSX.Element|null}
 */
export function renderEndTimeCell(formattedEndTime, endRelease, eventLevel, colClass) {

  const tooltipContent = getEndTimeTooltip(formattedEndTime, endRelease, eventLevel);
  const displayValue = formattedEndTime || "-";

  return (
    <div data-testid="ehdtr-endtime" className={colClass}>
        <StyledTooltip data-testid="ehdtr-endtime-tooltip" content={tooltipContent}>
          <span className="relative">{displayValue}</span>
        </StyledTooltip>
    </div>
  );
}

/**
 * Get the current time formatted as a datetime-local string.
 * Useful as a default value for HTML <input type="datetime-local"> fields.
 *
 * @returns {string} Current time in "YYYY-MM-DDTHH:mm" format.
 */
export function getCurrentTimeValue() {
  return convertDateToLocal(new Date(), false, true);
}

/**
 * Check if an end time is valid (must be after the start time).
 *
 * @param {string|Date} start - The event start time.
 * @param {string|Date} end - The proposed end time.
 * @returns {boolean} True if the end time is after the start time, false otherwise.
 */
export function isValidEndTime(start, end) {
  if (!start || !end) return true;
  return new Date(end) > new Date(start);
}

/**
 * Construct an event object for stopping or reopening an event.
 * Returns either a valid object or one with an `error` key for validation issues.
 *
 * @param {number} eventId - ID of the event to update.
 * @param {string} stopDate - The selected stop date in datetime-local format.
 * @param {string} eventStart - The original start date of the event.
 * @param {boolean} reopen - Whether this is a reopen action (true) or a stop (false).
 * @returns {{event_id: number, end_time: string} | {error: string}} Event object or error.
 */
export function buildStopEventObject(eventId, stopDate, eventStart, reopen = false) {
  if (reopen) {
    return {
      event_id: eventId,
      end_time: '__CLEAR__'
    };
  }

  if (!isValidEndTime(eventStart, stopDate)) {
    return {
      error: "End time must be after the start time."
    };
  }

  return {
    event_id: eventId,
    end_time: convertInputToTimezoneAwareISO(stopDate)
  };
}
/**
* Gets a consistent colour for an event type based on its title and ID.
*
* @param {string} title - The event type title (e.g., "Environment Issue", "Other")
* @param {number} eventTypeId - The unique event type ID from the database
* @returns {string} Colour name i.e. "red-300", "blue", "green-700", etc.
*/
function getEventTypeColour(title, eventTypeId) {
  return getEntityColour(title, eventTypeId); 
}

/**
 * Groups API events by area name and issue ID.
 * @param {TestEvent[]} events
 * @param {string} [defaultAreaName="Unknown Area"] - Default area name if additional_id is not present.
 * @returns {Map<string, Map<string, TestEvent[]>>}
 */
export function buildEventMap(events, defaultAreaName = "Unknown Area") {
  const eventMap = new Map();

  events.forEach((event) => {
    const area = event.additional_id || defaultAreaName; // Use the additionalId from the event or default to defaultArea

    if (!eventMap.has(area)) {
      eventMap.set(area, new Map());
    }

    const areaMap = eventMap.get(area);
    if (!areaMap.has(event.test_name)) {
      areaMap.set(event.test_name, []);
    }

    areaMap.get(event.test_name).push(event);
  });

  return eventMap;
}

/**
 * Generic function to filter events to only include those for items that currently exist
 * @param {string} pageName - The name of the page/test
 * @param {Array} currentItems - Array of current item objects (violations, issues, etc.)
 * @param {string} keyField - The field name to use as the key (e.g., 'help', 'name', 'id')
 * @param {Map} eventMap - The event map to filter from
 * @returns {Array} - Filtered array of events
 */
export function getEventsForCurrentItems(pageName, currentItems = [], keyField = 'help', eventMap = new Map()) {
  if (!pageName || !currentItems || !Array.isArray(currentItems) || !eventMap || !(eventMap instanceof Map)) {
    console.warn("Invalid parameters passed to getActiveEventsForPage");
    return [];
  }

  const pageEventsMap = eventMap.get(pageName) ?? new Map();
  const activeEvents = [];
  
  // Only include events for items that currently exist
  currentItems.forEach(item => {
    const itemKey = item[keyField];
    if (itemKey) {
      const itemEvents = pageEventsMap.get(itemKey) || [];
      activeEvents.push(...itemEvents);
    }
  });
  
  return activeEvents;
};